/**
 * Localhost-only frontend dev auth bypass (no MongoDB / backend required).
 * Never active in production builds or on non-local hostnames.
 */

import { AUTH_TOKEN_KEY, normalizeLoginEmail } from './authUtils';

export const DEV_AUTH_EMAIL = 'dev@fundelite.local';
export const DEV_AUTH_PASSWORD = 'DevTest123!';
export const DEV_AUTH_TOKEN_PREFIX = 'dev_local_bypass_';
export const DEV_AUTH_USER_KEY = 'fundelite_dev_auth_user';
export const DEV_FUNDGENIUS_CLIENTS_PATH = '/platform/fundgenius-hq/clients';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export function isLocalDevHostname() {
  if (typeof window === 'undefined') return false;
  return LOCAL_HOSTS.has(window.location.hostname);
}

/** Strict guard for frontend-only bypass (requires explicit env flag). */
export function isFrontendDevAuthBypassEnabled() {
  if (process.env.NODE_ENV === 'production') return false;
  if (!isLocalDevHostname()) return false;
  return process.env.REACT_APP_DEV_AUTH_ENABLED === 'true'
    || process.env.REACT_APP_DEV_AUTH_ENABLED === '1';
}

/** UI hint visibility (same guards as bypass). */
export function isDevAuthEnabled() {
  return isFrontendDevAuthBypassEnabled();
}

export function logDevAuthWarning() {
  if (!isFrontendDevAuthBypassEnabled()) return;
  if (typeof console !== 'undefined') {
    console.warn('Local dev auth enabled. Do not use in production.');
  }
}

export function isDevAuthToken(token) {
  return typeof token === 'string' && token.startsWith(DEV_AUTH_TOKEN_PREFIX);
}

export function buildDevAuthUser() {
  return {
    user_id: 'usr_dev_local_platform',
    email: DEV_AUTH_EMAIL,
    name: 'Local Dev Platform Owner',
    first_name: 'Local',
    last_name: 'Dev',
    role: 'platform_owner',
    plan: 'platform',
    onboarding_completed: true,
    subscription_tier: 'platform_owner',
    tier: 'platform_owner',
    agency_id: 'agency_internal_001',
    workspace_id: 'ws_staff_001',
    portal_type: 'platform_console',
    email_verified: true,
    requires_email_verification: false,
  };
}

export function tryDevAuthLogin(email, password) {
  if (!isFrontendDevAuthBypassEnabled()) return null;
  const normalized = normalizeLoginEmail(email);
  if (normalized !== DEV_AUTH_EMAIL || password !== DEV_AUTH_PASSWORD) return null;

  logDevAuthWarning();
  return {
    token: `${DEV_AUTH_TOKEN_PREFIX}${Date.now()}`,
    user: buildDevAuthUser(),
  };
}

export function persistDevAuthSession({ token, user }) {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(DEV_AUTH_USER_KEY, JSON.stringify(user));
}

export function readDevAuthSession() {
  if (!isFrontendDevAuthBypassEnabled()) return null;
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!isDevAuthToken(token)) return null;
  try {
    const raw = localStorage.getItem(DEV_AUTH_USER_KEY);
    const user = raw ? JSON.parse(raw) : buildDevAuthUser();
    if (!user?.user_id) return null;
    return { token, user };
  } catch {
    return null;
  }
}

export function clearDevAuthSession() {
  localStorage.removeItem(DEV_AUTH_USER_KEY);
}

/** Mock full SSN for UI testing when backend audit endpoints are unavailable. */
export function getDevMockSsnFull(ssnLast4) {
  const last4 = String(ssnLast4 || '6789').replace(/\D/g, '').slice(-4) || '6789';
  return `123-45-${last4}`;
}
